import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { usePoolMembersApi } from './config';
import { fetchPool, removePoolMember } from './config/poolApi';
import { fetchTagCatalog } from '../ats-candidates/config/tagApi';
import { parseTableParams } from '../../utils/Utils';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import Button from '../../components/ui/Button';
import SelectionBar, { selectionColumn } from './components/SelectionBar';
import InviteToPostingDrawer from './components/InviteToPostingDrawer';
import InviteResultPanel from './components/InviteResultPanel';

const PoolDescription = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// Davet gövdesine giden aday kimliği: üyenin EN GÜNCEL aday satırı (SPEC F2
// AtsCandidatePoolMember.CandidateId). Üye kimliği (`id`) yalnız çıkarma içindir.
export const memberCandidateId = (row) => row.candidateId;

export default function AtsCandidatePoolDetailsPage() {
  const { t } = useTranslation();
  const { id: poolId } = useParams();
  const history = useHistory();

  const [pool, setPool] = useState(null);
  const [tagCatalog, setTagCatalog] = useState([]);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [removeDialog, setRemoveDialog] = useState({ isOpen: false, row: null });
  const [removing, setRemoving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  // Tabloya verilen GÜNCEL kolonlar — süzgeç indeksleri bunlara göredir.
  const columnsRef = useRef([]);

  useEffect(() => {
    let alive = true;
    fetchPool(poolId).then((p) => { if (alive) setPool(p); });
    fetchTagCatalog().then(({ items }) => { if (alive) setTagCatalog(items); });
    return () => { alive = false; };
  }, [poolId]);

  const {
    fetching, loadFailed, data, pagination, columns: baseColumns, refresh,
  } = usePoolMembersApi(poolId, queryParams, tagCatalog);

  // Seçim SAYFA kapsamlıdır: sorgu ya da veri değişince düşer.
  useEffect(() => { setSelectedIds([]); }, [data, queryParams]);

  const toggle = useCallback((cid) => {
    setSelectedIds((ids) => (ids.includes(cid) ? ids.filter((x) => x !== cid) : [...ids, cid]));
  }, []);

  const openProfile = useCallback((row) => {
    const cid = row && memberCandidateId(row);
    if (cid) history.push(`/ats/candidates/${cid}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    // İlk kolon (seçim) süzgeçsizdir; parseTableParams kolon İNDEKSİYLE eşler, bu
    // yüzden taban kolonlar DEĞİL tabloya verilen kolonlar gönderilir.
    setQueryParams(parseTableParams(tableState, columnsRef.current));
  }, []);

  const handleFilterConfirm = useCallback((next) => setFilterList(next), []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removeDialog.row) return;
    setRemoving(true);
    const ok = await removePoolMember(poolId, removeDialog.row.id);
    setRemoving(false);
    setRemoveDialog({ isOpen: false, row: null });
    if (ok) refresh();
  }, [removeDialog.row, poolId, refresh]);

  const buildActions = useCallback((row) => [
    { type: 'details', label: t('ats.candidatePool.openProfile'), onClick: () => openProfile(row) },
    { type: 'divider' },
    {
      type: 'delete',
      label: t('ats.candidatePool.removeMember'),
      onClick: () => setRemoveDialog({ isOpen: true, row }),
    },
  ], [t, openProfile]);

  const columns = useMemo(() => [
    selectionColumn({ data, selectedIds, onToggle: toggle, idOf: memberCandidateId }),
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <span onClick={(e) => e.stopPropagation()} role="presentation">
              <OptionsMenu actions={buildActions(row)} row={row} />
            </span>
          );
        },
      },
    },
  ], [baseColumns, data, selectedIds, toggle, buildActions]);

  columnsRef.current = columns;

  const handleInviteResult = useCallback((result) => {
    // Adlar davet ANINDAKİ satırlardan sabitlenir (liste sonra değişebilir).
    const names = data.reduce((acc, r) => ({ ...acc, [memberCandidateId(r)]: r.fullName }), {});
    setInviteResult({ ...result, names });
    setSelectedIds([]);
  }, [data]);

  const poolName = pool?.name || '...';

  return (
    <div>
      <Breadcrumbs items={[
        { label: t('ats.candidatePool.pageTitle'), path: '/ats/candidate-pools' },
        { label: poolName },
      ]} />

      {pool?.description && <PoolDescription>{pool.description}</PoolDescription>}

      <ConfirmDialog
        isOpen={removeDialog.isOpen}
        title={t('ats.candidatePool.removeMember')}
        message={t('ats.candidatePool.removeMemberConfirm', {
          name: removeDialog.row?.fullName || '',
          pool: poolName,
        })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveDialog({ isOpen: false, row: null })}
        loading={removing}
      />

      <InviteToPostingDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        candidateIds={selectedIds}
        poolId={poolId}
        onResult={handleInviteResult}
      />

      {loadFailed && <LoadErrorBanner message={t('ats.candidatePool.membersLoadFailed')} />}

      <InviteResultPanel
        result={inviteResult}
        nameOf={(cid) => inviteResult?.names?.[cid]}
        onDismiss={() => setInviteResult(null)}
      />

      <SelectionBar count={selectedIds.length} onClear={() => setSelectedIds([])}>
        <Button type="button" data-role="pool-invite-open" onClick={() => setInviteOpen(true)}>
          {t('ats.candidatePool.inviteToPosition')}
        </Button>
      </SelectionBar>

      <DataTable
        title={t('ats.candidatePool.membersTitle', { name: poolName })}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        onRowClick={openProfile}
      />
    </div>
  );
}
